// src/pages/AdminDashboard.js
//
// Admin-only overview: one row per authenticated user (tax_subjects) OR
// per anonymous session (orphan calculator_sessions/triage_sessions row
// with no tax_subject_id). Deliberately NOT grouped for anonymous rows —
// there is no cross-session identity signal anywhere in this schema for
// a visitor who never signed in (tax_subjects only gets created by
// claim-session), so each anonymous row is its own isolated session, not
// a person. See get-admin-overview/index.ts's own header comment.
//
// Visually reuses AdvisorDashboard.js's existing advisor-* CSS classes
// wholesale (advisor-page, advisor-table, advisor-td, advisor-chips,
// etc.) rather than introducing a parallel admin-* stylesheet — this
// table has the same shape (rows, chips, muted sub-labels), just
// different columns and no status-editing affordance.

import React, { useEffect, useState, useCallback } from 'react'
import { createClient } from '@supabase/supabase-js'
import { useAuth } from '../context/AuthContext'
import { getAdvisorProductLabel } from '../components/HomeLink'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function ProductChips({ products, row }) {
  if (!products || products.length === 0) return <span style={{ color: 'var(--faint)' }}>—</span>
  const urlFor = (product) => product === 'triage' ? row.triage_result_url : row.calculator_result_url
  return (
    <div className="advisor-chips">
      {products.map(({ product, count }) => {
        const url = urlFor(product)
        const label = `${getAdvisorProductLabel(product)}${count > 1 ? ` (${count})` : ''}`
        return url ? (
          <a key={product} className="advisor-chip" href={url} target="_blank" rel="noreferrer" title="Open result">
            {label} ↗
          </a>
        ) : (
          <span key={product} className="advisor-chip">{label}</span>
        )
      })}
    </div>
  )
}

function IncomeTypeChips({ types }) {
  if (!types || types.length === 0) return <span style={{ color: 'var(--faint)' }}>—</span>
  return (
    <div className="advisor-chips">
      {types.map(t => <span key={t} className="advisor-chip advisor-chip-muted">{t}</span>)}
    </div>
  )
}

function formatEstimate(row) {
  if (row.irpf_gross == null && row.estimate_low == null) return '—'
  const value = row.irpf_gross != null
    ? `€${row.irpf_gross.toLocaleString()}`
    : `€${row.estimate_low.toLocaleString()}–${row.estimate_high.toLocaleString()}`
  return row.estimate_published ? value : `${value} (draft)`
}

function formatResidency(row) {
  const parts = []
  if (row.spain_residency_status) parts.push(row.spain_residency_status.replace(/_/g, ' '))
  if (row.citizenship_status) parts.push(row.citizenship_status.replace(/_/g, ' '))
  return parts.length ? parts.join(' · ') : '—'
}

function formatReferrer(referrer) {
  if (!referrer) return '—' // empty string means "direct" (no referrer) — same as null here, nothing to show
  try {
    return new URL(referrer).hostname.replace(/^www\./, '')
  } catch {
    return referrer // not a parseable URL for some reason — show raw rather than hide it
  }
}

export default function AdminDashboard() {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [outreachOnly, setOutreachOnly] = useState(false)
  const [deletingKey, setDeletingKey] = useState(null)
  const [deleteErrors, setDeleteErrors] = useState({})
  // Ticket 020 QA fix: admin-delete-row can return HTTP 200 with
  // authAccountDeleted: false (all app-table data already irreversibly
  // deleted, but the final auth.admin.deleteUser() call failed). That is
  // a legitimate partial-failure the backend surfaces deliberately — this
  // banner is what actually shows it to the admin, since the row is gone
  // from the table by then. Modeled on AdvisorDashboard.js's updateError
  // banner (same .advisor-error / .advisor-error-close pattern).
  const [authDeleteWarning, setAuthDeleteWarning] = useState(null)

  const loadOverview = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setLoadError(null)

    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) {
      setLoadError('Not authenticated.')
      setLoading(false)
      return
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-admin-overview`,
        { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
      )
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Failed to load overview')
      setRows(body.users || [])
    } catch (err) {
      setLoadError(`Failed to load users: ${err.message}`)
    }

    setLoading(false)
  }, [user])

  useEffect(() => { loadOverview() }, [loadOverview])

  // Ticket 020: hard-delete a person (kind: 'subject', full cascade
  // server-side — see admin-delete-row/index.ts) or a single orphan
  // anonymous session (kind: anon_*). Irreversible, so window.confirm()
  // guards it — reusing SubjectsPage.js's existing confirm-then-delete
  // pattern rather than building a new modal (plan.md D4). Row is removed
  // from local `rows` state on success; no full refetch needed.
  const deleteRowKey = (row) => `${row.kind}-${row.id}`

  async function handleDelete(row) {
    const key = deleteRowKey(row)
    const label = row.email || (row.kind === 'subject' ? 'this subject' : 'this anonymous session')
    if (!window.confirm(`Permanently delete ${label} and all their data? This cannot be undone.`)) return

    setDeletingKey(key)
    setDeleteErrors(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })

    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) {
      setDeleteErrors(prev => ({ ...prev, [key]: 'Not authenticated.' }))
      setDeletingKey(null)
      return
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-delete-row`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ kind: row.kind, id: row.id }),
        }
      )
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Failed to delete')
      setRows(prev => prev.filter(r => deleteRowKey(r) !== key))
      if (row.kind === 'subject' && body.authAccountDeleted === false) {
        setAuthDeleteWarning(
          `${label}'s app data was deleted, but their Supabase Auth login could NOT be removed (${body.reason || 'unknown reason'}). ` +
          `This person can still log in and their email is NOT safely reusable for a fresh signup until the Auth account is removed manually.`
        )
      }
    } catch (err) {
      setDeleteErrors(prev => ({ ...prev, [key]: err.message }))
    }

    setDeletingKey(null)
  }

  const anonCount = rows.filter(r => r.kind !== 'subject').length
  const authCount = rows.filter(r => r.kind === 'subject').length
  const outreachCount = rows.filter(r => r.marketing_opt_in === true && r.email).length

  const visibleRows = outreachOnly
    ? rows.filter(r => r.marketing_opt_in === true && r.email)
    : rows

  return (
    <div className="advisor-page">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 className="advisor-page-title">All Users</h1>
        <p className="u-subtext">{authCount} authenticated · {anonCount} anonymous sessions</p>
      </div>

      <label className="u-row-md" style={{ marginBottom: '1rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={outreachOnly}
          onChange={e => setOutreachOnly(e.target.checked)}
        />
        <span className="u-subtext">Only show opted-in users with an email ({outreachCount})</span>
      </label>

      {loadError && (
        <div className="advisor-error">
          <span>{loadError}</span>
        </div>
      )}

      {authDeleteWarning && (
        <div className="advisor-error">
          <span>{authDeleteWarning}</span>
          <button className="advisor-error-close" onClick={() => setAuthDeleteWarning(null)}>×</button>
        </div>
      )}

      <div className="advisor-table-wrap admin-table-wrap">
        {loading ? (
          <div className="advisor-loading">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="advisor-empty">
            <div className="advisor-empty-text">No users match.</div>
          </div>
        ) : (
          <table className="advisor-table">
            <thead>
              <tr className="advisor-thead-row">
                {['User', 'Products', 'Income / Estimate', 'Income types', 'Residency / Citizenship', 'Partner', 'Marketing', 'Partner consent', 'Referrer', 'Device', 'Created', 'Last updated', 'Actions'].map(h => (
                  <th key={h} className="advisor-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => {
                const isAnon = row.kind !== 'subject'
                const sameDate = row.created_at && row.updated_at &&
                  row.created_at.slice(0, 10) === row.updated_at.slice(0, 10)

                return (
                  <tr key={`${row.kind}-${row.id}`} className="advisor-row">
                    <td className="advisor-td">
                      <div className="u-label-base">
                        {row.email || (isAnon ? 'Anonymous' : 'Unnamed subject')}
                      </div>
                      {isAnon && <div className="u-name-sub">{row.id.slice(0, 8)}…</div>}
                    </td>
                    <td className="advisor-td" style={{ minWidth: 140 }}>
                      <ProductChips products={row.products} row={row} />
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {formatEstimate(row)}
                    </td>
                    <td className="advisor-td" style={{ minWidth: 160 }}>
                      <IncomeTypeChips types={row.income_types} />
                    </td>
                    <td className="advisor-td u-text-faint-xs" style={{ minWidth: 180 }}>
                      {formatResidency(row)}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {row.partner_name || '—'}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {row.marketing_opt_in === true ? 'Opted in' : row.marketing_opt_in === false ? 'Declined' : '—'}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {row.partner_data_consent === true ? 'Consented' : row.partner_data_consent === false ? 'No / unasked' : '—'}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {formatReferrer(row.referrer)}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {row.device_type || '—'}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="advisor-td u-text-faint-xs">
                      {sameDate ? '—' : formatDate(row.updated_at)}
                    </td>
                    <td className="advisor-td">
                      {row.is_admin ? (
                        // Safety fix: never show a delete button for an admin
                        // account — real enforcement is server-side in
                        // admin-delete-row/index.ts, this is just the UX half.
                        <span className="u-text-faint-xs">Admin account</span>
                      ) : (
                        <button
                          type="button"
                          className="admin-delete-btn"
                          onClick={() => handleDelete(row)}
                          disabled={deletingKey === deleteRowKey(row)}
                        >
                          {deletingKey === deleteRowKey(row) ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                      {deleteErrors[deleteRowKey(row)] && (
                        <div className="admin-delete-error">{deleteErrors[deleteRowKey(row)]}</div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}